/**
 * FloatingAnnotationsLayer
 *
 * Renders floating annotation cards (comments + track changes) positioned
 * at the same vertical level as the text they reference — Google Docs style.
 *
 * This component renders inside the editor's scroll container so cards
 * scroll naturally with the document. No manual scroll synchronization needed.
 */

import React from "react";
import { Editor } from "@tiptap/core";
import { CommentData } from "./TipTapEditor";
import { TrackChangeData } from "./TrackChangesPanel";
import { useAnnotationPositions } from "./hooks/useAnnotationPositions";
import FloatingAnnotationCard from "./FloatingAnnotationCard";
import "./CSS/FloatingAnnotations.css";

export interface FloatingAnnotationsLayerProps {
  editor: Editor;

  // Comments
  comments: CommentData[];
  activeCommentId: string | null;
  showComments: boolean;
  currentUser?: string;
  aiEditingCommentId?: string | null;
  onCommentClick: (id: string) => void;
  onDeleteComment: (id: string) => void;
  onResolveComment?: (id: string) => void;
  onEditComment?: (id: string, newContent: string) => void;
  onReplyToComment: (id: string, content: string, author: string) => void;
  onAIEditFromComment?: (id: string) => void;

  // Track changes
  trackChanges: TrackChangeData[];
  activeChangeId: string | null;
  showTrackChanges: boolean;
  onChangeClick: (id: string) => void;
  onAcceptChange: (id: string) => void;
  onRejectChange: (id: string) => void;

  readOnly?: boolean;

  // Pending comment support
  pendingCommentId?: string | null;
  onSubmitPendingComment?: (content: string) => void;
  onCancelPendingComment?: () => void;
}

const FloatingAnnotationsLayer: React.FC<FloatingAnnotationsLayerProps> = ({
  editor,
  comments,
  activeCommentId,
  showComments,
  currentUser = "User",
  aiEditingCommentId,
  onCommentClick,
  onDeleteComment,
  onResolveComment,
  onEditComment,
  onReplyToComment,
  onAIEditFromComment,
  trackChanges,
  activeChangeId,
  showTrackChanges,
  onChangeClick,
  onAcceptChange,
  onRejectChange,
  readOnly = false,
  pendingCommentId,
  onSubmitPendingComment,
  onCancelPendingComment,
}) => {
  const { positions, measureRef } = useAnnotationPositions(
    editor,
    comments,
    trackChanges,
    activeCommentId,
    activeChangeId,
    showComments,
    showTrackChanges,
    pendingCommentId
  );

  const hasAnnotations = positions.length > 0;

  if (!hasAnnotations) return null;

  return (
    <div className="floating-annotations-layer">
      {/* Positioned annotation cards (including pending comment) */}
      {positions.map((pos) => (
        <div
          key={pos.id}
          className="floating-card-wrapper"
          style={{
            position: "absolute",
            top: pos.adjustedTop,
            left: 0,
            width: "100%",
          }}
          ref={(el) => measureRef(pos.id, el)}
        >
          {/* Connecting line when card is pushed away from its anchor */}
          {Math.abs(pos.adjustedTop - pos.targetTop) > 4 && (
            <div
              className="floating-card-connector"
              style={{
                position: "absolute",
                left: -8,
                top: 0,
                width: 8,
                height: Math.abs(pos.adjustedTop - pos.targetTop),
                transform: pos.adjustedTop > pos.targetTop ? `translateY(-${pos.adjustedTop - pos.targetTop}px)` : undefined,
              }}
            >
              <svg width="8" height={Math.abs(pos.adjustedTop - pos.targetTop)} style={{ overflow: "visible" }}>
                <line
                  x1="0" y1="0"
                  x2="0" y2={Math.abs(pos.adjustedTop - pos.targetTop)}
                  stroke="#d0d0d0"
                  strokeWidth="1"
                  strokeDasharray="3,2"
                />
              </svg>
            </div>
          )}

          {/* Pending comment card (input for new comment) */}
          {pos.kind === "comment" && !pos.comment && pendingCommentId === pos.id && (
            <PendingCommentCardInline
              currentUser={currentUser}
              onSubmit={onSubmitPendingComment}
              onCancel={onCancelPendingComment}
            />
          )}

          {pos.kind === "comment" && pos.comment && (
            <FloatingAnnotationCard
              kind="comment"
              comment={pos.comment}
              isActive={activeCommentId === pos.id}
              readOnly={readOnly}
              currentUser={currentUser}
              aiEditingCommentId={aiEditingCommentId}
              onCommentClick={onCommentClick}
              onDeleteComment={onDeleteComment}
              onResolveComment={onResolveComment}
              onEditComment={onEditComment}
              onReplyToComment={onReplyToComment}
              onAIEditFromComment={onAIEditFromComment}
            />
          )}

          {pos.kind === "trackChange" && pos.trackChange && (
            <FloatingAnnotationCard
              kind="trackChange"
              trackChange={pos.trackChange}
              isActive={activeChangeId === pos.id}
              readOnly={readOnly}
              onChangeClick={onChangeClick}
              onAcceptChange={onAcceptChange}
              onRejectChange={onRejectChange}
            />
          )}
        </div>
      ))}
    </div>
  );
};

// ────────────────────────────────────────────
// Pending Comment Card (inline input for new comments)
// Position is handled by the parent wrapper via useAnnotationPositions.
// ────────────────────────────────────────────

const PendingCommentCardInline: React.FC<{
  currentUser: string;
  onSubmit?: (content: string) => void;
  onCancel?: () => void;
}> = ({ currentUser, onSubmit, onCancel }) => {
  const [text, setText] = React.useState("");
  const inputRef = React.useRef<HTMLTextAreaElement>(null);

  React.useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (text.trim() && onSubmit) {
      onSubmit(text.trim());
      setText("");
    }
  };

  return (
    <div className="floating-card floating-card-comment active">
      <div className="floating-card-header">
        <strong className="floating-card-author">{currentUser}</strong>
        <span className="floating-card-date">New comment</span>
      </div>
      <form onSubmit={handleSubmit}>
        <textarea
          ref={inputRef}
          className="floating-card-pending-input"
          rows={3}
          placeholder="Write your comment… (Esc to cancel)"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Escape" && onCancel) onCancel(); }}
        />
        <div className="floating-card-pending-actions">
          <button type="submit" className="btn btn-primary btn-sm" disabled={!text.trim()}>
            Comment
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default FloatingAnnotationsLayer;
